import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Building2, Download, Power, PowerOff, RefreshCw } from 'lucide-react';
import { listHotels, setHotelActive } from '@/lib/api/admin';
import { describeError } from '@/lib/api/client';
import type { AdminHotel } from '@/lib/api/types';
import { downloadCsv } from '@/lib/csv';
import { formatDate, formatNumber, formatRelative, plural } from '@/lib/format';
import { useDebounced, useUrlParam } from '@/lib/hooks';
import { IdChip } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { FilterSelect } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page';
import { Switch } from '@/components/ui/switch';
import { TableToolbar } from '@/components/ui/toolbar';
import { toast } from '@/components/ui/toast';
import { useOrganizations } from '@/features/organizations/use-organizations';

/**
 * Every hotel on the platform, with the one lever the admin has here: the
 * active flag. An inactive hotel stops answering on its booking page and in
 * its organization's PMS; its data stays. A row opens the hotel's own page.
 */
export function HotelsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const hotels = useQuery({ queryKey: ['admin', 'hotels'], queryFn: listHotels });
  const organizations = useOrganizations();

  const [q, setQ] = useUrlParam('q');
  const [status, setStatus] = useUrlParam('status', 'all');
  const [channels, setChannels] = useUrlParam('channels', 'all');
  const [problems, setProblems] = useUrlParam('problems', 'all');
  const [org, setOrg] = useUrlParam('org', 'all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const query = useDebounced(q.trim().toLowerCase(), 150);
  const filtersActive = !!q || status !== 'all' || channels !== 'all' || problems !== 'all' || org !== 'all';
  const reset = () => { setQ(''); setStatus('all'); setChannels('all'); setProblems('all'); setOrg('all'); };

  const filtered = useMemo(() => {
    let rows = hotels.data ?? [];
    if (query) rows = rows.filter((h) => `${h.name} ${h.city} ${h.country} ${organizations.label(h.organizationId)}`.toLowerCase().includes(query) || h.id.startsWith(query) || h.organizationId.startsWith(query));
    if (status !== 'all') rows = rows.filter((h) => h.isActive === (status === 'active'));
    if (channels !== 'all') rows = rows.filter((h) => (h.connectedChannels > 0) === (channels === 'with'));
    if (problems === 'errors') rows = rows.filter((h) => !!h.syncError);
    if (org !== 'all') rows = rows.filter((h) => h.organizationId === org);
    return rows;
  }, [hotels.data, query, status, channels, problems, org, organizations]);

  const toggle = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => setHotelActive(id, isActive),
    onSuccess: (_, v) => {
      toast('success', v.isActive ? 'Отель включён' : 'Отель выключен');
      qc.invalidateQueries({ queryKey: ['admin', 'hotels'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: (err) => toast('error', 'Не удалось изменить', describeError(err)),
  });

  // Bulk: the API is per-hotel, so this is N sequential calls with a tally at the end.
  const bulk = useMutation({
    mutationFn: async (isActive: boolean) => {
      let ok = 0; const failed: string[] = [];
      for (const id of selected) {
        try { await setHotelActive(id, isActive); ok += 1; } catch { failed.push(id); }
      }
      return { ok, failed, isActive };
    },
    onSuccess: ({ ok, failed, isActive }) => {
      toast(failed.length ? 'error' : 'success', `${isActive ? 'Включено' : 'Выключено'}: ${ok}`, failed.length ? `Не удалось: ${failed.length}` : undefined);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['admin', 'hotels'] });
      qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });

  const exportCsv = () => downloadCsv(`hotels-${new Date().toISOString().slice(0, 10)}.csv`,
    ['id', 'Отель', 'Город', 'Страна', 'Организация', 'organizationId', 'Номера', 'Каналы', 'Последний синк', 'Ошибка синка', 'Активен', 'Создан'],
    filtered.map((h) => [h.id, h.name, h.city, h.country, organizations.label(h.organizationId), h.organizationId, h.totalRooms, h.connectedChannels, h.lastSyncAt, h.syncError, h.isActive ? 'да' : 'нет', h.createdAt]));

  const columns: Column<AdminHotel>[] = [
    { key: 'name', header: 'Отель', sortValue: (h) => h.name, render: (h) => (
      <>
        <div className="font-semibold text-ink">{h.name}</div>
        <div className="text-xs text-muted">{[h.city, h.country].filter(Boolean).join(', ')}</div>
        {h.syncError && <div className="mt-1 flex items-start gap-1 text-xs text-danger"><AlertCircle size={12} className="mt-0.5 shrink-0" /><span className="line-clamp-1" title={h.syncError}>{h.syncError}</span></div>}
      </>
    ) },
    { key: 'org', header: 'Организация', sortValue: (h) => organizations.label(h.organizationId), stopClick: true, hideBelow: 'md', render: (h) => (
      <>
        <Link to={`/organizations/${h.organizationId}`} className="block max-w-[200px] truncate text-xs font-medium text-ink hover:text-accent-ink hover:underline">{organizations.label(h.organizationId)}</Link>
        <div className="mt-0.5"><IdChip id={h.organizationId} /></div>
      </>
    ) },
    { key: 'totalRooms', header: 'Номера', align: 'right', sortValue: (h) => h.totalRooms, render: (h) => formatNumber(h.totalRooms) },
    { key: 'connectedChannels', header: 'Каналы', sortValue: (h) => h.connectedChannels, render: (h) => (
      <span className="flex items-center gap-1.5 whitespace-nowrap text-xs">
        <RefreshCw size={12} className={h.connectedChannels > 0 ? 'text-ok' : 'text-faint'} />
        {h.connectedChannels > 0 ? plural(h.connectedChannels, ['канал', 'канала', 'каналов']) : <span className="text-muted">нет</span>}
      </span>
    ) },
    { key: 'lastSyncAt', header: 'Синк', sortValue: (h) => h.lastSyncAt, hideBelow: 'lg', className: 'whitespace-nowrap text-xs text-muted', render: (h) => h.lastSyncAt ? <span title={h.lastSyncAt}>{formatRelative(h.lastSyncAt)}</span> : '—' },
    { key: 'createdAt', header: 'Создан', sortValue: (h) => h.createdAt, hideBelow: 'lg', className: 'whitespace-nowrap text-xs text-muted', render: (h) => formatDate(h.createdAt) },
    { key: 'isActive', header: 'Активен', align: 'center', width: 'w-24', sortValue: (h) => h.isActive, stopClick: true, render: (h) => (
      <Switch checked={h.isActive} onChange={(v) => toggle.mutate({ id: h.id, isActive: v })} disabled={toggle.isPending && toggle.variables?.id === h.id} label={h.isActive ? 'Выключить отель' : 'Включить отель'} />
    ) },
  ];

  return (
    <>
      <PageHeader
        title="Отели"
        subtitle={hotels.data ? `${plural(hotels.data.length, ['отель', 'отеля', 'отелей'])} во всех организациях` : undefined}
      />

      <TableToolbar
        search={{ value: q, onChange: setQ, placeholder: 'Название, город, организация или id' }}
        shown={filtered.length}
        total={hotels.data?.length}
        onReset={filtersActive ? reset : null}
        actions={<Button variant="outline" size="sm" icon={<Download size={14} />} onClick={exportCsv} disabled={filtered.length === 0}>CSV</Button>}
      >
        <FilterSelect ariaLabel="Статус" value={status} onChange={setStatus} options={[{ value: 'all', label: 'Все статусы' }, { value: 'active', label: 'Активные' }, { value: 'inactive', label: 'Выключенные' }]} />
        <FilterSelect ariaLabel="Каналы" value={channels} onChange={setChannels} options={[{ value: 'all', label: 'Любые каналы' }, { value: 'with', label: 'С каналами' }, { value: 'without', label: 'Без каналов' }]} />
        <FilterSelect ariaLabel="Проблемы" value={problems} onChange={setProblems} options={[{ value: 'all', label: 'С ошибками и без' }, { value: 'errors', label: 'Только с ошибкой синка' }]} />
        <FilterSelect ariaLabel="Организация" value={org} onChange={setOrg} options={[{ value: 'all', label: 'Все организации' }, ...organizations.orgs.map((o) => ({ value: o.id, label: organizations.label(o.id) }))]} />
      </TableToolbar>

      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-accent/30 bg-accent-soft px-3 py-2 text-sm text-accent-ink animate-fade-in">
          <span className="font-semibold">Выбрано: {selected.size}</span>
          <span className="flex-1" />
          <Button size="sm" variant="outline" icon={<Power size={13} />} loading={bulk.isPending} onClick={() => bulk.mutate(true)}>Включить</Button>
          <Button size="sm" variant="outline" icon={<PowerOff size={13} />} loading={bulk.isPending} onClick={() => bulk.mutate(false)}>Выключить</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Снять выбор</Button>
        </div>
      )}

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(h) => h.id}
        defaultSort={{ key: 'createdAt', dir: 'desc' }}
        loading={hotels.isPending}
        error={hotels.error}
        onRetry={() => hotels.refetch()}
        empty={{ icon: <Building2 size={28} />, title: hotels.data?.length ? 'Ничего не подошло под фильтры' : 'Отели ещё не зарегистрированы', action: filtersActive ? <Button size="sm" variant="ghost" onClick={reset}>Сбросить фильтры</Button> : undefined }}
        onRowClick={(h) => navigate(`/hotels/${h.id}`)}
        rowClassName={(h) => (h.isActive ? undefined : 'opacity-70')}
        selectable={{ selected, onChange: setSelected }}
      />
    </>
  );
}
